import { Box } from "@mui/material";
import { EmojiEvents, TrendingFlat, TrendingDown, Paid, Percent } from "@mui/icons-material";
import { DashboardMetricCard } from "./DashboardMetricCard";
import { useResponsive } from "../../../core/hooks/useResponsive";

function formatInr(n, compact = false) {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: compact ? 0 : 2,
  }).format(num);
}

function formatNum(n) {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (Number.isNaN(num)) return String(n);
  return String(Math.round(num));
}

function formatPct(n) {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (Number.isNaN(num)) return String(n);
  return `${num.toFixed(2)}%`;
}

/**
 * Closed-trade performance — same `data` shape as API `tradePerformance`.
 *
 * Layout by width:
 * - phone      Realized P/L as a hero tile carrying Success rate as a pill, then the
 *              three trade counts 3-up (they're short numbers, so they fit)
 * - 480-1023   dense tiles, 3-up
 * - >=1024     original five-across row
 */
export const TradePerformance = ({ data, singleColumn = false }) => {
  const { isPhone, isCompact } = useResponsive();
  const d = data || {};
  const totalProfit = parseFloat(d.totalProfit);
  const successRate = parseFloat(d.successRate);
  const plAccent = totalProfit >= 0 ? "success" : "error";
  const srAccent = successRate >= 50 ? "success" : "warning";

  if (isPhone && !singleColumn) {
    return (
      <Box sx={{ display: "grid", gap: 1 }}>
        <DashboardMetricCard
          hero
          label="Realized P/L"
          value={formatInr(d.totalProfit)}
          badge={Number.isFinite(successRate) ? `${successRate.toFixed(1)}% win` : null}
          badgeColor={successRate >= 50 ? "#16a34a" : "#d97706"}
          sublabel="Closed trades, this book"
          icon={Paid}
          accent={plAccent}
          valueColor={totalProfit >= 0 ? "success.main" : "error.main"}
          highlight
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          <DashboardMetricCard dense label="Total" value={formatNum(d.totalTrades)} icon={EmojiEvents} accent="primary" />
          <DashboardMetricCard dense label="Won" value={formatNum(d.profitTrades)} icon={TrendingFlat} accent="success" />
          <DashboardMetricCard dense label="Lost" value={formatNum(d.lossTrades)} icon={TrendingDown} accent="error" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.25,
        gridTemplateColumns: singleColumn
          ? "1fr"
          : {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
      }}
    >
      <DashboardMetricCard dense={isCompact} label="Total trades" value={formatNum(d.totalTrades)} icon={EmojiEvents} accent="primary" />
      <DashboardMetricCard dense={isCompact} label="Winning closes" value={formatNum(d.profitTrades)} icon={TrendingFlat} accent="success" />
      <DashboardMetricCard dense={isCompact} label="Losing closes" value={formatNum(d.lossTrades)} icon={TrendingDown} accent="error" />
      <DashboardMetricCard dense={isCompact} label="Realized P/L" value={formatInr(d.totalProfit)} icon={Paid} accent={plAccent} highlight />
      <DashboardMetricCard dense={isCompact} label="Success rate" value={formatPct(d.successRate)} icon={Percent} accent={srAccent} highlight />
    </Box>
  );
};
